import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Check, Loader2, RefreshCw, User, Users, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WEDDING } from "@/lib/constants";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Guest Entry Component
function GuestEntry({ index, guest, onChange }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ delay: index * 0.1 }}
      className="p-4 bg-white/40 border border-w-sand rounded-lg space-y-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-w-sage/20 flex items-center justify-center">
          <span className="text-xs font-body text-w-sage font-medium">{index + 1}</span>
        </div>
        <span className="font-body text-sm text-w-charcoal">
          Ospite {index + 1}{index === 0 ? " (tu)" : ""}
        </span>
      </div>
      
      <div>
        <Label className="font-body text-xs text-w-muted mb-1 block">Nome e Cognome *</Label>
        <Input
          value={guest.nome_cognome}
          onChange={(e) => onChange({ ...guest, nome_cognome: e.target.value })}
          placeholder="Nome Cognome"
          className="bg-white/60 border-w-sand focus:ring-w-sage focus:border-w-sage font-body text-sm placeholder:text-w-sand"
        />
      </div>
      
      <div>
        <Label className="font-body text-xs text-w-muted mb-1.5 block">Presenza</Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange({ ...guest, conferma_presenza: true })}
            className={`flex-1 py-2 border text-xs font-body transition-colors duration-200 rounded ${
              guest.conferma_presenza
                ? 'bg-w-sage text-white border-w-sage'
                : 'bg-white/60 text-w-charcoal border-w-sand hover:border-w-sage'
            }`}
          >
            Ci sarà
          </button>
          <button
            type="button"
            onClick={() => onChange({ ...guest, conferma_presenza: false })}
            className={`flex-1 py-2 border text-xs font-body transition-colors duration-200 rounded ${
              !guest.conferma_presenza
                ? 'bg-w-charcoal text-white border-w-charcoal'
                : 'bg-white/60 text-w-charcoal border-w-sand hover:border-w-charcoal'
            }`}
          >
            Mi spiace, no
          </button>
        </div>
      </div>
      
      {guest.conferma_presenza && (
        <div>
          <Label className="font-body text-xs text-w-muted mb-1 block">Allergie / Esigenze alimentari</Label>
          <Input
            value={guest.allergie}
            onChange={(e) => onChange({ ...guest, allergie: e.target.value })}
            placeholder="Celiachia, intolleranze..."
            className="bg-white/60 border-w-sand focus:ring-w-sage focus:border-w-sage font-body text-sm placeholder:text-w-sand"
          />
        </div>
      )}
    </motion.div>
  );
}

export default function RSVPForm() {
  // Invite type: null = not selected, 'single' or 'group'
  const [inviteType, setInviteType] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  // Single person form state
  const [singleForm, setSingleForm] = useState({
    nome_cognome: "", email: "", telefono: "",
    conferma_presenza: true, allergie: "", messaggio: "",
  });
  
  // Group form state
  const [groupSize, setGroupSize] = useState(2);
  const [guests, setGuests] = useState([]);
  const [groupContact, setGroupContact] = useState({
    email: "", telefono: "", messaggio: ""
  });
  const [showFinalStep, setShowFinalStep] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [wasConfirmed, setWasConfirmed] = useState(true);

  // Initialize guests when group size changes
  useEffect(() => {
    setGuests(prev => {
      const newGuests = [...prev];
      while (newGuests.length < groupSize) {
        newGuests.push({ nome_cognome: "", conferma_presenza: true, allergie: "" });
      }
      return newGuests.slice(0, groupSize);
    });
  }, [groupSize]);

  const updateSingle = (field, value) => setSingleForm(prev => ({ ...prev, [field]: value }));
  const updateGuest = (index, guest) => {
    setGuests(prev => {
      const newGuests = [...prev];
      newGuests[index] = guest;
      return newGuests;
    });
  };

  const resetForm = () => {
    setInviteType(null);
    setIsFormOpen(false);
    setSingleForm({
      nome_cognome: "", email: "", telefono: "",
      conferma_presenza: true, allergie: "", messaggio: "",
    });
    setGroupSize(2);
    setGuests([]);
    setGroupContact({ email: "", telefono: "", messaggio: "" });
    setShowFinalStep(false);
    setSubmitted(false);
  };

  // Check if all guests have names filled
  const allGuestsFilled = guests.every(g => g.nome_cognome.trim() !== "");

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!singleForm.nome_cognome || !singleForm.email) {
      toast.error("Nome e email sono obbligatori!");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/rsvp`, {
        ...singleForm,
        invite_type: 'single',
        numero_accompagnatori: 0
      });
      setWasConfirmed(singleForm.conferma_presenza);
      setSubmitted(true);
      toast.success("RSVP inviata con successo!");
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    }
    setLoading(false);
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (!groupContact.email) {
      toast.error("L'email è obbligatoria!");
      return;
    }
    if (!allGuestsFilled) {
      toast.error("Compila il nome di tutti gli ospiti!");
      return;
    }
    setLoading(true);
    try {
      // Submit each guest as a separate row
      await axios.post(`${API}/rsvp-group`, {
        guests: guests.map(g => ({
          nome_cognome: g.nome_cognome,
          conferma_presenza: g.conferma_presenza,
          allergie: g.allergie || ""
        })),
        email: groupContact.email,
        telefono: groupContact.telefono || "",
        messaggio: groupContact.messaggio || "",
        group_size: groupSize
      });
      setWasConfirmed(guests.some(g => g.conferma_presenza));
      setSubmitted(true);
      toast.success("RSVP di gruppo inviata con successo!");
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <section id="rsvp" className="section-padding bg-w-sand/30" data-testid="rsvp-section">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center py-16"
        >
          <div className={`w-16 h-16 ${wasConfirmed ? 'bg-w-sage/20' : 'bg-w-gold/20'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <Check className={`w-8 h-8 ${wasConfirmed ? 'text-w-sage' : 'text-w-gold'}`} />
          </div>
          <h2 className="font-heading text-3xl text-w-charcoal">
            {wasConfirmed ? "Grazie!" : "Ci mancherai!"}
          </h2>
          <p className="font-body text-w-muted mt-3">
            {wasConfirmed 
              ? "La tua conferma e' stata registrata. Non vediamo l'ora di festeggiare insieme!"
              : "Ci dispiace che non possiate esserci con noi, grazie per averci avvisati!"
            }
          </p>
          <button
            onClick={resetForm}
            data-testid="rsvp-reset"
            className="mt-8 inline-flex items-center gap-2 border border-w-sage text-w-sage font-body text-xs uppercase tracking-widest px-6 py-3 rounded-full hover:bg-w-sage/10 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Invia un'altra risposta
          </button>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="rsvp" className="section-padding bg-w-sand/30" data-testid="rsvp-section">
      <div className="max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-w-sage">Ci sarai?</p>
          <h2 className="font-heading text-4xl sm:text-5xl text-w-charcoal mt-2">Conferma Presenza</h2>
          <p className="font-body text-w-muted mt-3 text-sm md:text-base">
            Rispondi entro {WEDDING.deadline_rsvp}, ma se puoi va bene anche prima!
          </p>
        </motion.div>

        {/* Step 1: Invite Type Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-4"
        >
          <Label className="font-body text-sm text-w-charcoal block">
            Il tuo invito prevede una sola persona o più persone?
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="invite-single"
              onClick={() => {
                setInviteType('single');
                setIsFormOpen(true);
              }}
              className={`p-5 border-2 rounded-lg transition-all duration-200 flex flex-col items-center gap-3 ${
                inviteType === 'single'
                  ? 'border-w-sage bg-w-sage/10'
                  : 'border-w-sand bg-white/40 hover:border-w-sage/50'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                inviteType === 'single' ? 'bg-w-sage/20' : 'bg-w-sand/50'
              }`}>
                <User className={`w-6 h-6 ${inviteType === 'single' ? 'text-w-sage' : 'text-w-muted'}`} />
              </div>
              <span className={`font-body text-sm ${inviteType === 'single' ? 'text-w-charcoal font-medium' : 'text-w-muted'}`}>
                Una sola persona
              </span>
            </button>
            
            <button
              type="button"
              data-testid="invite-group"
              onClick={() => {
                setInviteType('group');
                setIsFormOpen(true);
              }}
              className={`p-5 border-2 rounded-lg transition-all duration-200 flex flex-col items-center gap-3 ${
                inviteType === 'group'
                  ? 'border-w-sage bg-w-sage/10'
                  : 'border-w-sand bg-white/40 hover:border-w-sage/50'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                inviteType === 'group' ? 'bg-w-sage/20' : 'bg-w-sand/50'
              }`}>
                <Users className={`w-6 h-6 ${inviteType === 'group' ? 'text-w-sage' : 'text-w-muted'}`} />
              </div>
              <span className={`font-body text-sm ${inviteType === 'group' ? 'text-w-charcoal font-medium' : 'text-w-muted'}`}>
                Invito di gruppo
              </span>
            </button>
          </div>
        </motion.div>

        {/* Single Person Form (Accordion) */}
        <AnimatePresence>
          {inviteType === 'single' && isFormOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-6">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(!isFormOpen)}
                  className="w-full flex items-center justify-between p-4 bg-white/40 border border-w-sand rounded-t-lg"
                >
                  <span className="font-body text-sm text-w-charcoal">Compila i tuoi dati</span>
                  {isFormOpen ? <ChevronUp className="w-4 h-4 text-w-muted" /> : <ChevronDown className="w-4 h-4 text-w-muted" />}
                </button>
                
                <form onSubmit={handleSingleSubmit} className="p-4 bg-white/20 border border-t-0 border-w-sand rounded-b-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nome" className="font-body text-sm text-w-charcoal mb-1.5 block">Nome e Cognome *</Label>
                      <Input
                        id="nome"
                        data-testid="rsvp-nome"
                        value={singleForm.nome_cognome}
                        onChange={(e) => updateSingle("nome_cognome", e.target.value)}
                        placeholder="Mario Rossi"
                        className="bg-white/60 border-w-sand focus:ring-w-sage focus:border-w-sage font-body placeholder:text-w-sand"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-body text-sm text-w-charcoal mb-1.5 block">Email *</Label>
                      <Input
                        id="email"
                        data-testid="rsvp-email"
                        type="email"
                        value={singleForm.email}
                        onChange={(e) => updateSingle("email", e.target.value)}
                        placeholder="mario@email.com"
                        className="bg-white/60 border-w-sand focus:ring-w-sage focus:border-w-sage font-body placeholder:text-w-sand"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="telefono" className="font-body text-sm text-w-charcoal mb-1.5 block">Telefono</Label>
                    <Input
                      id="telefono"
                      data-testid="rsvp-telefono"
                      value={singleForm.telefono}
                      onChange={(e) => updateSingle("telefono", e.target.value)}
                      placeholder="+39 333 1234567"
                      className="bg-white/60 border-w-sand focus:ring-w-sage focus:border-w-sage font-body placeholder:text-w-sand"
                    />
                  </div>

                  <div>
                    <Label className="font-body text-sm text-w-charcoal mb-2 block">Conferma Presenza</Label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        data-testid="rsvp-si"
                        onClick={() => updateSingle("conferma_presenza", true)}
                        className={`flex-1 py-3 border text-sm font-body transition-colors duration-200 ${
                          singleForm.conferma_presenza
                            ? 'bg-w-sage text-white border-w-sage'
                            : 'bg-white/60 text-w-charcoal border-w-sand hover:border-w-sage'
                        }`}
                      >
                        Ci sarò!
                      </button>
                      <button
                        type="button"
                        data-testid="rsvp-no"
                        onClick={() => updateSingle("conferma_presenza", false)}
                        className={`flex-1 py-3 border text-sm font-body transition-colors duration-200 ${
                          !singleForm.conferma_presenza
                            ? 'bg-w-charcoal text-white border-w-charcoal'
                            : 'bg-white/60 text-w-charcoal border-w-sand hover:border-w-charcoal'
                        }`}
                      >
                        Mi dispiace, no
                      </button>
                    </div>
                  </div>

                  {singleForm.conferma_presenza && (
                    <div>
                      <Label htmlFor="allergie" className="font-body text-sm text-w-charcoal mb-1.5 block">
                        Allergie o esigenze alimentari
                      </Label>
                      <Textarea
                        id="allergie"
                        data-testid="rsvp-allergie"
                        value={singleForm.allergie}
                        onChange={(e) => updateSingle("allergie", e.target.value)}
                        placeholder="Celiachia, intolleranze, veganismo..."
                        className="bg-white/60 border-w-sand focus:ring-w-sage focus:border-w-sage font-body min-h-[80px] placeholder:text-w-sand"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="messaggio" className="font-body text-sm text-w-charcoal mb-1.5 block">
                      Un messaggio per gli sposi
                    </Label>
                    <Textarea
                      id="messaggio"
                      data-testid="rsvp-messaggio"
                      value={singleForm.messaggio}
                      onChange={(e) => updateSingle("messaggio", e.target.value)}
                      placeholder="Auguri, consigli non richiesti, battute..."
                      className="bg-white/60 border-w-sand focus:ring-w-sage focus:border-w-sage font-body min-h-[80px] placeholder:text-w-sand"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="rsvp-submit"
                    disabled={loading}
                    className="w-full bg-w-sage hover:bg-w-sage/90 text-white font-body uppercase tracking-widest text-xs py-6 rounded-full btn-wedding"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send className="w-3.5 h-3.5" />
                        Invia conferma
                      </span>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Group Form */}
        <AnimatePresence>
          {inviteType === 'group' && isFormOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <form onSubmit={handleGroupSubmit} className="pt-6 space-y-6">
                {/* Group Size Input */}
                <div className="p-6 bg-white/40 border border-w-sand rounded-lg">
                  <Label className="font-body text-sm text-w-charcoal mb-4 block text-center">
                    Quante persone fanno parte del tuo gruppo? (te compreso/a)
                  </Label>
                  <div className="flex justify-center items-center gap-4">
                    {/* Minus Button */}
                    <button
                      type="button"
                      onClick={() => setGroupSize(prev => Math.max(prev - 1, 2))}
                      disabled={groupSize <= 2}
                      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                        groupSize <= 2 
                          ? 'border-w-sand/50 text-w-sand/50 cursor-not-allowed' 
                          : 'border-w-sage text-w-sage hover:bg-w-sage hover:text-white'
                      }`}
                      data-testid="group-size-minus"
                    >
                      <span className="text-2xl font-light leading-none">−</span>
                    </button>
                    
                    {/* Number Display */}
                    <div 
                      className="w-20 h-16 flex items-center justify-center bg-white/80 border border-w-sage/30 rounded-lg"
                      data-testid="group-size-display"
                    >
                      <span className="font-heading text-4xl md:text-5xl text-w-charcoal">{groupSize}</span>
                    </div>
                    
                    {/* Plus Button */}
                    <button
                      type="button"
                      onClick={() => setGroupSize(prev => Math.min(prev + 1, 10))}
                      disabled={groupSize >= 10}
                      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                        groupSize >= 10 
                          ? 'border-w-sand/50 text-w-sand/50 cursor-not-allowed' 
                          : 'border-w-sage text-w-sage hover:bg-w-sage hover:text-white'
                      }`}
                      data-testid="group-size-plus"
                    >
                      <span className="text-2xl font-light leading-none">+</span>
                    </button>
                  </div>
                  <p className="font-body text-xs text-w-muted text-center mt-3">
                    Minimo 2, massimo 10 persone
                  </p>
                </div>

                {/* Guest Entries */}
                <div className="space-y-3">
                  <Label className="font-body text-sm text-w-charcoal block">
                    Inserisci i dati di ogni ospite
                  </Label>
                  <AnimatePresence mode="popLayout">
                    {guests.map((guest, index) => (
                      <GuestEntry
                        key={index}
                        index={index}
                        guest={guest}
                        onChange={(updatedGuest) => updateGuest(index, updatedGuest)}
                      />
                    ))}
                  </AnimatePresence>
                </div>

                {/* Final Step - Contact Info */}
                <AnimatePresence>
                  {allGuestsFilled && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="p-5 bg-w-sage/10 border border-w-sage/30 rounded-lg space-y-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-5 h-5 text-w-sage" />
                        <span className="font-body text-sm text-w-charcoal font-medium">
                          Ottimo! Ancora un ultimo passaggio
                        </span>
                      </div>
                      
                      <div>
                        <Label className="font-body text-sm text-w-charcoal mb-1.5 block">Email di contatto *</Label>
                        <Input
                          type="email"
                          value={groupContact.email}
                          onChange={(e) => setGroupContact(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="referente@email.com"
                          className="bg-white/80 border-w-sage/30 focus:ring-w-sage focus:border-w-sage font-body placeholder:text-w-sand"
                        />
                      </div>
                      
                      <div>
                        <Label className="font-body text-sm text-w-charcoal mb-1.5 block">Telefono</Label>
                        <Input
                          value={groupContact.telefono}
                          onChange={(e) => setGroupContact(prev => ({ ...prev, telefono: e.target.value }))}
                          placeholder="+39 333 1234567"
                          className="bg-white/80 border-w-sage/30 focus:ring-w-sage focus:border-w-sage font-body placeholder:text-w-sand"
                        />
                      </div>
                      
                      <div>
                        <Label className="font-body text-sm text-w-charcoal mb-1.5 block">Un messaggio per gli sposi</Label>
                        <Textarea
                          value={groupContact.messaggio}
                          onChange={(e) => setGroupContact(prev => ({ ...prev, messaggio: e.target.value }))}
                          placeholder="Auguri dal gruppo!"
                          className="bg-white/80 border-w-sage/30 focus:ring-w-sage focus:border-w-sage font-body min-h-[80px] placeholder:text-w-sand"
                        />
                      </div>

                      <Button
                        type="submit"
                        data-testid="rsvp-group-submit"
                        disabled={loading || !groupContact.email}
                        className="w-full bg-w-sage hover:bg-w-sage/90 text-white font-body uppercase tracking-widest text-xs py-6 rounded-full btn-wedding"
                      >
                        {loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <Send className="w-3.5 h-3.5" />
                            Invia conferma di gruppo
                          </span>
                        )}
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
